import Big from 'big.js'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import TransactionButton from 'src/common/components/Buttons/TransactionButton'
import useBalance from 'src/common/contracts/hooks/token/useBalance'
import useActiveOrDefaultChain from 'src/common/hooks/web3/useActiveOrDefaultChain'
import TokenDisplay from 'src/common/TokenDisplay'
import Icon from 'src/common/ui-kit/Icon/Icon'
import { safeDiv } from 'src/common/utils/big-utils'
import { isBlockOffer } from 'src/common/utils/offers'
import {
  buyTokenAmountAtom,
  buyTokenAtom,
  offersToTakeAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
  successfulTxHashAtom,
} from '../stores/trade-store'
import useSwapTrade from './SwapTrade/hooks/useSwapTrade'
import { TransactionResult } from 'src/common/contracts/hooks/useExecuteTx'
import useSmartAccount from 'src/common/api-clients/safe-protocol-kit/useSmartAccount'

const TakeSingleOffer = () => {
  const navigate = useNavigate()
  const chain = useActiveOrDefaultChain()

  const { smartAccountInfo } = useSmartAccount()

  const {
    takeOffersTx,
    gasEstimateInEther,
    loading: swapTradeLoading,
  } = useSwapTrade()

  const [sellTokenAmount, setSellTokenAmount] = useAtom(sellTokenAmountAtom)
  const [buyTokenAmount, setBuyTokenAmount] = useAtom(buyTokenAmountAtom)
  const sellToken = useAtomValue(sellTokenAtom)
  const buyToken = useAtomValue(buyTokenAtom)
  const [offersToTake, setOffersToTake] = useAtom(offersToTakeAtom)
  const offer = offersToTake[0]
  const setSuccessfulTxHash = useSetAtom(successfulTxHashAtom)

  const { balance, isLoading: balanceLoading } = useBalance({
    owner: smartAccountInfo?.address,
    token: sellToken,
  })

  const isBalanceEnough = balance?.gte(sellTokenAmount)
  const price = safeDiv(offer.amountOut, offer.amountIn)
  const maxSellAmount = isBlockOffer(offer)
    ? null
    : balance?.gt(offer.availableAmount.mul(price))
    ? offer.availableAmount.mul(price)
    : balance

  const handleBack = () => navigate(-1)

  const offerFields = [
    // ['Max buying amount:', displayAmount(offer.availableAmount)],
    [
      'Network cost',
      gasEstimateInEther
        ? `${gasEstimateInEther.toString()} ${chain.nativeCurrency?.symbol}`
        : null,
    ],
    // ['Total to pay', `${displayAmount(sellTokenAmount)} ${sellToken?.symbol}`],
  ]

  const handleTxSuccess = ({ hash }: TransactionResult) => {
    setSuccessfulTxHash(hash)
    navigate('/trade/take/success')
  }

  const handleChangeSellTokenAmount = (value: Big) => {
    setSellTokenAmount(value)
    setBuyTokenAmount(safeDiv(value, price))
    setOffersToTake([{ ...offer, toTake: value }])
  }

  return (
    <>
      <div className='paper p-4 mb-4'>
        <div className='flex items-center mb-4'>
          <Icon
            name='arrow-right'
            className='mr-4 rotate-180 cursor-pointer'
            onClick={handleBack}
          />
          <div className='typo-title2 text-main-accent'>Take offer</div>
        </div>
        <div className='flex flex-col gap-2'>
          <TokenDisplay
            readonly={isBlockOffer(offer)}
            token={sellToken}
            amount={sellTokenAmount}
            label={'Sell'}
            numericFieldProps={{
              type: 'big',
              onChange: handleChangeSellTokenAmount,
            }}
            maxValue={maxSellAmount}
          />
          <TokenDisplay
            readonly
            token={buyToken}
            amount={buyTokenAmount}
            label={'Buy'}
          />
        </div>
        <hr className='my-4' />
        <div className='flex flex-col gap-2'>
          {offerFields.map(
            ([text, value]) =>
              text && (
                <div key={text} className='flex justify-between'>
                  <div className='typo-label1'>{text}</div>
                  <div className='typo-label1 text-main-accent'>
                    {value || <div className='loader w-3 h-3' />}
                  </div>
                </div>
              )
          )}
        </div>
      </div>

      <TransactionButton
        className='w-full'
        transaction={takeOffersTx}
        loading={swapTradeLoading || balanceLoading}
        onSuccess={handleTxSuccess}
        disabled={!isBalanceEnough}
      >
        {/* Place Order */}
        {isBalanceEnough ? 'Take' : 'Insufficient balance'}
      </TransactionButton>
    </>
  )
}

export default TakeSingleOffer
